import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'

import { RoutingPaths } from 'app/routes/routes'

import {
  type KyrgyzstanOwnerCounters,
  KyrgyzstanWebviewOwnerApi,
} from 'shared/api/kyrgyzstan/webview-owner'
import { Button } from 'shared/ui/button'
import { Skeleton } from 'shared/ui/skeleton'

import s from './styles.module.scss'
import { CreateObjectEntryHintTour } from './ui'

let cachedCounters: KyrgyzstanOwnerCounters | null = null

export const CreateObjectEntry = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [counters, setCounters] = useState<KyrgyzstanOwnerCounters | null>(
    cachedCounters,
  )
  const [isLoading, setIsLoading] = useState(() => cachedCounters === null)

  useEffect(() => {
    const controller = new AbortController()

    const fetchCounters = async () => {
      try {
        const response = await KyrgyzstanWebviewOwnerApi.getCounters(
          controller.signal,
        )

        if (!controller.signal.aborted) {
          cachedCounters = response.data.data
          setCounters(cachedCounters)
        }
      } catch {
        if (!controller.signal.aborted && cachedCounters === null) {
          setCounters(null)
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchCounters()

    return () => {
      controller.abort()
    }
  }, [])

  if (isLoading) {
    return (
      <div className={s.skeleton}>
        <Skeleton
          width="100%"
          height={56}
          borderRadius={20}
        />
      </div>
    )
  }

  const hasObjects =
    Boolean(counters?.has_drafts) || Boolean(counters?.has_objects)
  const orderRequestsCount = counters?.order_requests_count ?? 0
  const shouldShowBadge = hasObjects && orderRequestsCount > 0
  const entryMode = hasObjects ? 'myObjects' : 'createObject'
  const entryConfig = {
    myObjects: {
      label: t('kyrgyzstan_block.my_objects.title'),
      path: RoutingPaths.kyrgyzstan.objects.path(),
      state: { orderRequestsCount },
    },
    createObject: {
      label: t('kyrgyzstan_block.create_object.entry.button'),
      path: RoutingPaths.kyrgyzstan.objects.create.path(),
      state: undefined,
    },
  }[entryMode]

  const handleClick = () => {
    navigate(entryConfig.path, {
      state: entryConfig.state,
    })
  }

  return (
    <div>
      <Button
        id="create-object-entry-button"
        borderRadius="md"
        onClick={handleClick}
        type="button"
        variant="white"
        className={clsx(s.button, 'semi-bold-16')}
      >
        <span>{entryConfig.label}</span>

        {shouldShowBadge ? (
          <span className={clsx(s.badge, 'bold-12')}>{orderRequestsCount}</span>
        ) : null}
      </Button>

      <CreateObjectEntryHintTour />
    </div>
  )
}
